import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Step,
    StepLabel,
    Stepper,
    Tooltip,
    Typography,
} from '@mui/material';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DropzoneAreaBase } from 'mui-file-dropzone';
import toast from 'react-hot-toast';
import Routes from '../../routes';
import { trakrefFontFamily } from '../../utils';
import { Grey1, Grey3, trakrefBlue } from '../../color_utils';

const STEPS = ['Select Category', 'Upload File', 'Validate & Import', 'Complete'];

const ACCEPTED_EXTENSIONS = ['.xls', '.xlsx'];

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// US 14916: Locations' single category is auto-selected; other import types plug in as stories land.
// noun: result-copy wording ("5 locations added") � the label alone can't be pluralized safely.
const IMPORT_TYPES = {
    locations: {
        label: 'Locations',
        noun: { singular: 'location', plural: 'locations' },
        categories: [{ name: 'Locations', templateFileName: 'ImportLocationsTemplate.xlsx' }],
        templateRoute: () => Routes.Location.GetImportLocationsTemplate(),
        importRoute: () => Routes.Location.BulkImportLocations(),
    },
    // US 14740/14741: only Systems is selectable this iteration; the rest are listed as Coming Soon.
    inventory: {
        label: 'Inventory',
        noun: { singular: 'system', plural: 'systems' },
        categories: [
            { name: 'Systems', templateFileName: 'ImportSystemsTemplate.xlsx' },
            { name: 'Cylinders', comingSoon: true },
            { name: 'SubSystems', comingSoon: true },
            { name: 'Leak Monitoring Devices', comingSoon: true },
        ],
        templateRoute: () => Routes.Asset.GetInventoryImportTemplate(),
        importRoute: () => Routes.Asset.ImportInventory(),
    },
};

// Per US 16876, the server names why it rejected a whole file so each reason gets its own copy.
const REJECTION_COPY = {
    CustomerIdMismatch: {
        title: 'Template does not match customer instance',
        body: 'Please resubmit with the correct template. No rows were imported.',
    },
    MissingCustomerId: {
        title: 'Template is missing a Customer ID',
        body: 'This file predates Customer ID validation. Download a fresh template and re-enter your data.',
    },
};

const DEFAULT_REJECTION_COPY = {
    title: 'Import is not possible',
    body: 'Please use the template and format provided. Accepted: .xls or .xlsx with the correct worksheet.',
};

export const downloadBase64Xlsx = (fileName, base64) => {
    const raw = base64.includes(',') ? base64.split(',').pop() : base64;
    const bytes = atob(raw);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i += 1) {
        buffer[i] = bytes.charCodeAt(i);
    }
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME }));
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const hasAcceptedExtension = (fileName) => (
    ACCEPTED_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext))
);

const formatFileSize = (bytes) => (
    bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} bytes`
);

const fontSx = { fontFamily: trakrefFontFamily };

// The page theme's primary is not the brand teal, so every themed control gets it explicitly.
const textBtnSx = { ...fontSx, color: trakrefBlue };
const containedBtnSx = {
    ...fontSx,
    backgroundColor: trakrefBlue,
    '&:hover': { backgroundColor: '#0A7482' },
};
const outlinedBtnSx = {
    ...fontSx,
    color: trakrefBlue,
    borderColor: 'rgba(14, 140, 156, 0.5)',
    '&:hover': { borderColor: trakrefBlue, backgroundColor: '#E6F4F6' },
};

const OUTCOMES = {
    success: {
        color: '#388E3C',
        Icon: CheckCircleOutlineIcon,
        title: 'Import Successful',
        subtitle: (noun) => `All ${noun.plural} were imported successfully.`,
        snack: (r, noun) => `Import complete. ${r.importedRows} ${r.importedRows === 1 ? noun.singular : noun.plural} added.`,
    },
    partial: {
        color: '#F58D23',
        Icon: WarningAmberIcon,
        title: 'Import Partially Successful',
        subtitle: () => 'Some rows had validation errors. Review the post-upload file to correct errors.',
        snack: (r) => `Import finished. ${r.importedRows} added, ${r.failedRows} need fixes.`,
    },
    failed: {
        color: '#F44336',
        Icon: ErrorOutlineIcon,
        title: 'Import Not Successful',
        subtitle: () => 'All rows had validation errors. Review the post-upload file to correct errors.',
        snack: null,
    },
    empty: {
        color: '#F58D23',
        Icon: WarningAmberIcon,
        title: 'No Data Rows Found',
        subtitle: () => 'The file matches the template but contains no data rows. Add your data below the header row and upload again.',
        snack: null,
    },
};

const SummaryTile = ({ label, value, color, backgroundColor }) => (
    <Box sx={{ flex: 1, padding: '14px', borderRadius: '6px', backgroundColor, textAlign: 'center' }}>
        <Typography sx={{ ...fontSx, fontSize: '24px', fontWeight: 700, color }}>{value}</Typography>
        <Typography sx={{ ...fontSx, fontSize: '12px', color }}>{label}</Typography>
    </Box>
);

SummaryTile.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
};

const FileRow = ({ name, meta, error, onRemove }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 16px',
            borderRadius: '6px',
            backgroundColor: '#F8F7F7',
            border: `1px solid ${error ? '#FFCDD2' : '#E1E9ED'}`,
        }}
    >
        <InsertDriveFileOutlinedIcon sx={{ color: error ? '#F44336' : Grey3, fontSize: 22 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ ...fontSx, fontSize: '14px', fontWeight: 500, color: Grey1 }}>{name}</Typography>
            <Typography sx={{ ...fontSx, fontSize: '12px', color: '#5A7F93' }}>{meta}</Typography>
        </Box>
        <IconButton size="small" aria-label="remove file" onClick={onRemove}>
            <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
    </Box>
);

FileRow.propTypes = {
    name: PropTypes.string.isRequired,
    meta: PropTypes.string.isRequired,
    error: PropTypes.bool,
    onRemove: PropTypes.func.isRequired,
};

const CategoryBar = ({ name }) => (
    <Box
        sx={{
            display: 'inline-block',
            backgroundColor: '#E6F4F6',
            color: '#0A7482',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: trakrefFontFamily,
            marginBottom: '14px',
        }}
    >
        Category: <strong>{name}</strong>
    </Box>
);

CategoryBar.propTypes = { name: PropTypes.string.isRequired };

const BulkImportModal = ({ open, onClose, importType }) => {
    const config = IMPORT_TYPES[importType];
    const [phase, setPhase] = useState('select');
    const [categoryIndex, setCategoryIndex] = useState(0);
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    // Locations name a rejection reason (US 16876); inventory only sends a message string.
    const [rejectionReason, setRejectionReason] = useState(null);
    const [structureMessage, setStructureMessage] = useState(null);
    const [processingCopy, setProcessingCopy] = useState({});
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const copyTimerRef = useRef(null);
    const dropzoneRef = useRef(null);
    // Synchronous re-entrancy guard: a rapid double-click beats React's re-render/disable.
    const busyRef = useRef(false);

    useEffect(() => {
        if (open) {
            setPhase('select');
            setCategoryIndex(0);
            setFile(null);
            setResult(null);
            setRejectionReason(null);
            setStructureMessage(null);
        }
        return () => clearTimeout(copyTimerRef.current);
    }, [open]);

    if (!config) return null;

    const category = config.categories[categoryIndex];
    const activeStep = { select: 0, upload: 1, review: 1, fileerror: 1, validating: 2, complete: 4 }[phase];
    const rejectionCopy = REJECTION_COPY[rejectionReason] ?? DEFAULT_REJECTION_COPY;

    const handleDownloadTemplate = async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        setDownloadingTemplate(true);
        try {
            const response = await fetch(config.templateRoute(), {
                method: 'GET',
                headers: { 'Accept': 'application/json' },
            });
            const json = await response.json();
            const payload = json?.data ?? json;
            if (!response.ok || !payload?.fileBytes) {
                throw new Error('Template download failed');
            }
            downloadBase64Xlsx(payload.fileName || category.templateFileName, payload.fileBytes);
            toast.success(`Template downloaded: ${payload.fileName || category.templateFileName}`, { duration: 4000 });
        } catch (e) {
            toast.error('Template download failed. Please try again.', { duration: 10000 });
        } finally {
            setDownloadingTemplate(false);
            busyRef.current = false;
        }
    };

    const handleFileAdded = (fileObjs) => {
        const added = fileObjs[0];
        setFile(added);
        setRejectionReason(null);
        setStructureMessage(null);
        setPhase(hasAcceptedExtension(added.file.name) ? 'review' : 'fileerror');
    };

    const handleUpload = async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        setPhase('validating');
        setProcessingCopy({
            title: 'Uploading file�',
            subtitle: 'Sending your file to Trakref.',
            caption: 'Uploading�',
        });
        copyTimerRef.current = setTimeout(() => setProcessingCopy({
            title: 'Verifying & importing�',
            subtitle: 'Checking file structure, then importing valid rows.',
            caption: 'Processing rows�',
        }), 1500);
        try {
            const response = await fetch(config.importRoute(), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fileName: file.file.name,
                    fileBytes: file.data,
                    contentType: file.file.type,
                }),
            });
            const json = await response.json();
            const payload = json?.data ?? json;
            clearTimeout(copyTimerRef.current);

            // rejectionReason must be checked too: the instance check returns it with
            // isStructureValid still true, so testing only the flag would report success.
            if (payload?.rejectionReason || payload?.isStructureValid === false) {
                setRejectionReason(payload?.rejectionReason ?? null);
                setStructureMessage(payload?.message || null);
                setPhase('fileerror');
                return;
            }
            if (!response.ok) {
                throw new Error(payload?.message || 'Import failed');
            }

            // Zero-row upload: skip the download � a header-only workbook adds nothing to the outcome copy.
            if (payload.resultFileBytes && payload.totalRows > 0) {
                downloadBase64Xlsx(payload.resultFileName || 'PostUpload.xlsx', payload.resultFileBytes);
            }
            setResult(payload);
            setPhase('complete');
        } catch (e) {
            clearTimeout(copyTimerRef.current);
            toast.error('Import failed. Please try again.', { duration: 10000 });
            setPhase('review');
        } finally {
            busyRef.current = false;
        }
    };

    const outcomeKey = result && (
        result.totalRows === 0 ? 'empty'
            : result.failedRows === 0 && result.importedRows > 0 ? 'success'
                : result.importedRows > 0 ? 'partial' : 'failed'
    );
    const outcome = outcomeKey && OUTCOMES[outcomeKey];

    const handleDone = () => {
        if (outcome?.snack) {
            toast.success(outcome.snack(result, config.noun), { duration: 4000 });
        }
        onClose();
    };

    const backToUpload = () => {
        setFile(null);
        setResult(null);
        setRejectionReason(null);
        setStructureMessage(null);
        setPhase('upload');
    };

    return (
        <Dialog
            open={open}
            onClose={phase === 'validating' ? undefined : onClose}
            PaperProps={{ sx: { width: 560, maxWidth: '95vw' } }}
        >
            <DialogTitle sx={{ ...fontSx, fontSize: '20px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {`Bulk Import ${config.label}`}
                <IconButton aria-label="close" onClick={onClose} disabled={phase === 'validating'}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stepper
                    activeStep={activeStep}
                    sx={{
                        marginBottom: '22px',
                        '& .MuiStepLabel-label': { ...fontSx, fontSize: '13px' },
                        '& .MuiStepIcon-root.Mui-active': { color: trakrefBlue },
                        '& .MuiStepIcon-root.Mui-completed': { color: trakrefBlue },
                    }}
                >
                    {STEPS.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {phase === 'select' && (
                    <>
                        <Alert severity="info" icon={false} sx={{ ...fontSx, fontSize: '13px', marginBottom: '14px', backgroundColor: '#F2F8F9', color: Grey1 }}>
                            <strong>Start with your category&apos;s template.</strong>{' '}
                            Download it, add your data, and upload the completed file.
                            Trakref verifies every import against its template.
                        </Alert>
                        {config.categories.map((cat, index) => {
                            const selected = categoryIndex === index;
                            const row = (
                                <Box
                                    key={cat.name}
                                    onClick={cat.comingSoon ? undefined : () => setCategoryIndex(index)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px 14px',
                                        marginBottom: '8px',
                                        border: `1px solid ${selected ? trakrefBlue : '#E1E9ED'}`,
                                        borderRadius: '6px',
                                        backgroundColor: selected ? '#E6F4F6' : '#FFFFFF',
                                        cursor: cat.comingSoon ? 'default' : 'pointer',
                                        opacity: cat.comingSoon ? 0.55 : 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: `2px solid ${selected ? trakrefBlue : '#C2CDD3'}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {selected && (
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: trakrefBlue }} />
                                        )}
                                    </Box>
                                    <Typography sx={{ ...fontSx, fontSize: '14px', fontWeight: 500, flex: 1, color: Grey1 }}>
                                        {cat.name}
                                    </Typography>
                                    {cat.comingSoon ? (
                                        <Typography sx={{ ...fontSx, fontSize: '12px', fontWeight: 500, color: Grey3 }}>
                                            Coming Soon
                                        </Typography>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={downloadingTemplate}
                                            startIcon={downloadingTemplate ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                                            onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(); }}
                                            sx={{ ...outlinedBtnSx, fontSize: '12px' }}
                                        >
                                            Template
                                        </Button>
                                    )}
                                </Box>
                            );
                            return cat.comingSoon ? (
                                <Tooltip key={cat.name} title="Coming Soon" arrow>
                                    {row}
                                </Tooltip>
                            ) : row;
                        })}
                    </>
                )}

                {phase === 'upload' && (
                    <>
                        <CategoryBar name={category.name} />
                        <Box
                            ref={dropzoneRef}
                            sx={{
                                position: 'relative',
                                // Dropzone root has no MuiDropzoneArea class in this version; target it positionally.
                                '& > div:first-of-type': {
                                    minHeight: '230px',
                                    backgroundColor: '#F8F7F7',
                                    border: '2px dashed #C2CDD3',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                },
                            }}
                        >
                            <DropzoneAreaBase
                                fileObjects={[]}
                                filesLimit={1}
                                showPreviewsInDropzone={false}
                                showAlerts={false}
                                onAdd={handleFileAdded}
                                dropzoneText=""
                                Icon={() => null}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    pointerEvents: 'none',
                                }}
                            >
                                <BackupOutlinedIcon sx={{ fontSize: 44, color: '#9AA7AD' }} />
                                <Typography sx={{ ...fontSx, fontSize: '16px', color: Grey1, marginTop: '6px' }}>
                                    Drag &amp; drop your file here, or{' '}
                                    <Box
                                        component="span"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            dropzoneRef.current?.querySelector('input[type="file"]')?.click();
                                        }}
                                        sx={{ color: trakrefBlue, fontWeight: 500, pointerEvents: 'auto', cursor: 'pointer' }}
                                    >
                                        browse
                                    </Box>
                                </Typography>
                                <Typography sx={{ ...fontSx, fontSize: '12px', color: Grey3 }}>
                                    {`Accepted: .xls, .xlsx � Template: ${category.templateFileName}`}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}

                {phase === 'review' && file && (
                    <>
                        <CategoryBar name={category.name} />
                        <FileRow
                            name={file.file.name}
                            meta={`${formatFileSize(file.file.size)} � ${category.name}`}
                            onRemove={backToUpload}
                        />
                        <Typography sx={{ ...fontSx, fontSize: '12px', color: Grey3, marginTop: '10px' }}>
                            Your file will be checked and verified after upload.
                        </Typography>
                    </>
                )}

                {phase === 'fileerror' && (
                    <>
                        {file && (
                            <FileRow
                                name={file.file.name}
                                meta={formatFileSize(file.file.size)}
                                error
                                onRemove={backToUpload}
                            />
                        )}
                        <Alert severity="error" sx={{ ...fontSx, marginTop: '12px' }}>
                            <AlertTitle sx={fontSx}>{rejectionCopy.title}</AlertTitle>
                            {rejectionReason ? rejectionCopy.body : (structureMessage || DEFAULT_REJECTION_COPY.body)}
                        </Alert>
                    </>
                )}

                {phase === 'validating' && (
                    <Box sx={{ textAlign: 'center', padding: '14px 0' }}>
                        <CircularProgress size={44} sx={{ marginBottom: '18px', color: trakrefBlue }} />
                        <Typography sx={{ ...fontSx, fontSize: '16px', fontWeight: 500 }}>
                            {processingCopy.title}
                        </Typography>
                        <Typography sx={{ ...fontSx, fontSize: '14px', color: '#5A7F93', marginBottom: '20px' }}>
                            {processingCopy.subtitle}
                        </Typography>
                        <LinearProgress sx={{ backgroundColor: '#EFF2F2', '& .MuiLinearProgress-bar': { backgroundColor: trakrefBlue } }} />
                        <Typography sx={{ ...fontSx, fontSize: '12px', color: Grey3, marginTop: '8px' }}>
                            {processingCopy.caption}
                        </Typography>
                    </Box>
                )}

                {phase === 'complete' && outcome && (
                    <>
                        <Box sx={{ textAlign: 'center', marginBottom: '18px' }}>
                            <outcome.Icon sx={{ fontSize: 46, color: outcome.color }} />
                            <Typography sx={{ ...fontSx, fontSize: '18px', fontWeight: 500, color: Grey1, marginTop: '8px' }}>
                                {outcome.title}
                            </Typography>
                            <Typography sx={{ ...fontSx, fontSize: '14px', color: '#5A7F93', maxWidth: 380, margin: '4px auto 0' }}>
                                {/* An "empty" result may carry a more specific server reason (e.g. all rows already imported). */}
                                {(outcomeKey === 'empty' && result.message) || outcome.subtitle(config.noun)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                            <SummaryTile label="Total" value={result.totalRows} color={Grey1} backgroundColor="#F8F7F7" />
                            <SummaryTile label="Imported" value={result.importedRows} color="#388E3C" backgroundColor="#EDF7ED" />
                            <SummaryTile label="Failed" value={result.failedRows} color="#F44336" backgroundColor="#FDEDED" />
                        </Box>
                        {result.resultFileName && result.totalRows > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 14px',
                                    borderRadius: '6px',
                                    backgroundColor: '#F8F7F7',
                                    border: '1px solid #E1E9ED',
                                }}
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: 16, color: Grey3 }} />
                                <Typography sx={{ ...fontSx, fontSize: '12px', color: Grey3 }}>
                                    Post-upload file downloaded: <strong>{result.resultFileName}</strong>
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ paddingBottom: '20px' }}>
                {phase === 'select' && (
                    <>
                        <Button sx={textBtnSx} onClick={onClose}>Cancel</Button>
                        <Button sx={containedBtnSx} variant="contained" onClick={() => setPhase('upload')}>Continue</Button>
                    </>
                )}
                {phase === 'upload' && (
                    <>
                        <Button sx={textBtnSx} onClick={() => setPhase('select')}>Back</Button>
                        <Button sx={containedBtnSx} variant="contained" disabled>Upload</Button>
                    </>
                )}
                {phase === 'review' && (
                    <>
                        <Button sx={textBtnSx} onClick={backToUpload}>Back</Button>
                        <Button sx={containedBtnSx} variant="contained" onClick={handleUpload}>Upload</Button>
                    </>
                )}
                {phase === 'fileerror' && (
                    <>
                        <Button sx={textBtnSx} onClick={onClose}>Cancel</Button>
                        <Button
                            sx={outlinedBtnSx}
                            variant="outlined"
                            disabled={downloadingTemplate}
                            startIcon={downloadingTemplate ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                            onClick={handleDownloadTemplate}
                        >
                            Download template
                        </Button>
                    </>
                )}
                {phase === 'complete' && (outcomeKey === 'failed' || outcomeKey === 'empty') && (
                    <>
                        <Button sx={textBtnSx} onClick={onClose}>Close</Button>
                        <Button sx={containedBtnSx} variant="contained" onClick={backToUpload}>Try again</Button>
                    </>
                )}
                {phase === 'complete' && outcomeKey !== 'failed' && outcomeKey !== 'empty' && (
                    <Button sx={containedBtnSx} variant="contained" onClick={handleDone}>Done</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

BulkImportModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    importType: PropTypes.oneOf(Object.keys(IMPORT_TYPES)).isRequired,
};

export default BulkImportModal;
